import { prisma, TermsAndConditionsEntityType } from "client";

/**
 * Get terms and conditions for an entity with all translations
 */
export const getTermsAndConditionsByEntity = async (
  entityType: TermsAndConditionsEntityType,
  entityId: string
) => {
  return await prisma.termsAndConditions.findFirst({
    where: { entityType, entityId },
    include: {
      translations: {
        include: {
          language: true,
        },
      },
    },
  });
};

/**
 * Get terms and conditions for a specific language and entity
 */
export const getTermsAndConditionsByEntityAndLanguage = async (
  entityType: TermsAndConditionsEntityType,
  entityId: string,
  languageId: string
) => {
  return await prisma.termsAndConditionsTranslations.findFirst({
    where: {
      termsAndConditions: {
        entityType,
        entityId,
      },
      languageId,
    },
    include: {
      language: true,
      termsAndConditions: true,
    },
  });
};

/**
 * Create or update terms and conditions with translations
 * @param entityType - Entity type
 * @param entityId - Entity ID
 * @param translations - Array of translations { languageId: string, content: string }
 */
export const upsertTermsAndConditionsByEntity = async (
  entityType: TermsAndConditionsEntityType,
  entityId: string,
  translations: Array<{ languageId: string; content: string }>
) => {
  const startTime = Date.now();

  try {
    // Find existing terms and conditions
    const existing = await prisma.termsAndConditions.findFirst({
      where: { entityType, entityId },
      include: { translations: true },
    });

    if (existing) {
      // Update existing translations and create new ones
      const translationPromises = translations.map(async (translation) => {
        const existingTranslation = existing.translations.find(
          (t) => t.languageId === translation.languageId
        );

        if (existingTranslation) {
          // Update existing translation
          return prisma.termsAndConditionsTranslations.update({
            where: { id: existingTranslation.id },
            data: { content: translation.content },
          });
        } else {
          // Create new translation
          return prisma.termsAndConditionsTranslations.create({
            data: {
              termsAndConditionsId: existing.id,
              languageId: translation.languageId,
              content: translation.content,
            },
          });
        }
      });

      await Promise.all(translationPromises);

      // Return updated terms and conditions with translations
      return await prisma.termsAndConditions.findFirst({
        where: { entityType, entityId },
        include: {
          translations: {
            include: {
              language: true,
            },
          },
        },
      });
    } else {
      // Create new terms and conditions with translations
      return await prisma.termsAndConditions.create({
        data: {
          entityType,
          entityId,
          translations: {
            create: translations.map((t) => ({
              languageId: t.languageId,
              content: t.content,
            })),
          },
        },
        include: {
          translations: {
            include: {
              language: true,
            },
          },
        },
      });
    }
  } catch (error) {
    console.error(
      `Database: Operation failed after ${Date.now() - startTime}ms:`,
      error
    );
    throw error;
  }
};

/**
 * Delete terms and conditions (will cascade delete all translations)
 */
export const deleteTermsAndConditionsByEntity = async (
  entityType: TermsAndConditionsEntityType,
  entityId: string
) => {
  const existing = await prisma.termsAndConditions.findFirst({
    where: { entityType, entityId },
    select: { id: true },
  });

  if (!existing) return null;

  return await prisma.termsAndConditions.delete({
    where: { id: existing.id },
  });
};

/**
 * Event-compatible wrappers for existing call sites.
 */
export const getTermsAndConditions = async (eventId: string) =>
  getTermsAndConditionsByEntity(TermsAndConditionsEntityType.EVENT, eventId);

export const getTermsAndConditionsByLanguage = async (
  eventId: string,
  languageId: string
) =>
  getTermsAndConditionsByEntityAndLanguage(
    TermsAndConditionsEntityType.EVENT,
    eventId,
    languageId
  );

export const upsertTermsAndConditions = async (
  eventId: string,
  translations: Array<{ languageId: string; content: string }>
) =>
  upsertTermsAndConditionsByEntity(
    TermsAndConditionsEntityType.EVENT,
    eventId,
    translations
  );

export const deleteTermsAndConditions = async (eventId: string) =>
  deleteTermsAndConditionsByEntity(TermsAndConditionsEntityType.EVENT, eventId);

export const getWhiteLabelTermsAndConditions = async (whiteLabelId: string) =>
  getTermsAndConditionsByEntity(
    TermsAndConditionsEntityType.WHITE_LABEL,
    whiteLabelId
  );

export const getWhiteLabelTermsAndConditionsByLanguage = async (
  whiteLabelId: string,
  languageId: string
) =>
  getTermsAndConditionsByEntityAndLanguage(
    TermsAndConditionsEntityType.WHITE_LABEL,
    whiteLabelId,
    languageId
  );

export const upsertWhiteLabelTermsAndConditions = async (
  whiteLabelId: string,
  translations: Array<{ languageId: string; content: string }>
) =>
  upsertTermsAndConditionsByEntity(
    TermsAndConditionsEntityType.WHITE_LABEL,
    whiteLabelId,
    translations
  );

export const deleteWhiteLabelTermsAndConditions = async (whiteLabelId: string) =>
  deleteTermsAndConditionsByEntity(
    TermsAndConditionsEntityType.WHITE_LABEL,
    whiteLabelId
  );

export const getOrganizationTermsAndConditions = async (organizationId: string) =>
  getTermsAndConditionsByEntity(
    TermsAndConditionsEntityType.ORGANIZATION,
    organizationId
  );

export const getOrganizationTermsAndConditionsByLanguage = async (
  organizationId: string,
  languageId: string
) =>
  getTermsAndConditionsByEntityAndLanguage(
    TermsAndConditionsEntityType.ORGANIZATION,
    organizationId,
    languageId
  );

export const upsertOrganizationTermsAndConditions = async (
  organizationId: string,
  translations: Array<{ languageId: string; content: string }>
) =>
  upsertTermsAndConditionsByEntity(
    TermsAndConditionsEntityType.ORGANIZATION,
    organizationId,
    translations
  );

export const deleteOrganizationTermsAndConditions = async (organizationId: string) =>
  deleteTermsAndConditionsByEntity(
    TermsAndConditionsEntityType.ORGANIZATION,
    organizationId
  );

/**
 * Delete a specific translation
 */
export const deleteTermsAndConditionsTranslation = async (
  translationId: string
) => {
  return await prisma.termsAndConditionsTranslations.delete({
    where: { id: translationId },
  });
};
